import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, ShieldCheck, Eye, ArrowLeft, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Users() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "viewer" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("Usuario creado");
      setForm({ name: "", email: "", password: "", role: "viewer" });
      load();
    } catch (err) { toast.error(apiErr(err)); } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar usuario?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Layout>
      <button onClick={() => nav("/")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft className="h-4 w-4" /> Volver
      </button>
      <h1 className="font-display text-3xl font-extrabold text-slate-900 mb-6">Usuarios</h1>

      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={create} className="bg-white border border-slate-200 rounded-lg p-5 h-fit" data-testid="user-form">
          <h3 className="font-display font-bold text-slate-900 flex items-center gap-2 mb-4">
            <UserPlus className="h-4 w-4 text-emerald-600" /> Nuevo usuario
          </h3>
          {["name", "email", "password"].map((f) => (
            <input key={f} data-testid={`user-${f}`}
              type={f === "password" ? "password" : f === "email" ? "email" : "text"} required
              value={form[f]} onChange={(e) => setForm({ ...form, [f]: e.target.value })}
              placeholder={f === "name" ? "Nombre" : f === "email" ? "Email" : "Contraseña"}
              className="w-full mb-3 px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm" />
          ))}
          <select data-testid="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="w-full mb-4 px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm bg-white">
            <option value="viewer">Solo lectura (ve solo ventas)</option>
            <option value="admin">Administrador (ve costos)</option>
          </select>
          <button type="submit" disabled={saving} data-testid="user-submit"
            className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold py-2 rounded-md transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Crear
          </button>
        </form>

        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg overflow-hidden">
          {users === null ? (
            <div className="p-6 text-slate-400">Cargando...</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="px-4 py-3">Nombre</th><th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Rol</th><th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">{u.name}</td>
                    <td className="px-4 py-3 text-slate-600">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${u.role === "admin" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                        {u.role === "admin" ? <ShieldCheck className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                        {u.role === "admin" ? "Admin" : "Lectura"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {u.id !== user?.id && (
                        <button data-testid={`delete-user-${u.id}`} onClick={() => del(u.id)}
                          className="text-slate-300 hover:text-red-500 transition-colors">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
}
